import { FormEvent, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { LockKeyhole, Mail } from "lucide-react";

const TRACK_LABS_LOGO_URL =
  "https://tracklabs-2rqxgdhv.manus.space/manus-storage/track-labs-logo-no-tagline_b38a3502.png";

export default function FirstLoginSetup() {
  const [, navigate] = useLocation();
  const setup = trpc.auth.completeFirstLoginSetup.useMutation();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  const returnPath = useMemo(() => {
    const value = new URLSearchParams(window.location.search).get("return");
    return value && value.startsWith("/") ? value : "/app";
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    try {
      await setup.mutateAsync({ newPassword, email });
      toast.success("Your account is ready.");
      navigate(returnPath);
    } catch (error: any) {
      toast.error(error?.message || "Unable to finish account setup.");
    }
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-cyan-50 px-4 py-10 text-slate-900">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-lg items-center justify-center">
        <Card className="w-full rounded-3xl border-white/70 bg-white/90 shadow-xl backdrop-blur">
          <CardHeader className="space-y-3 p-8 pb-5">
            <div className="flex h-14 w-full items-center justify-start">
              <img
                src={TRACK_LABS_LOGO_URL}
                alt="Track Labs"
                className="h-14 w-auto max-w-[220px] object-contain"
                decoding="async"
              />
            </div>
            <CardTitle className="text-2xl font-semibold">Finish setting up your account</CardTitle>
            <CardDescription className="leading-6">For your security, create a private password and save the email address where password-recovery instructions should be sent before entering your workspace.</CardDescription>
          </CardHeader>
          <CardContent className="p-8 pt-2">
            <form onSubmit={submit} className="space-y-5">
              <div className="space-y-2"><Label htmlFor="first-login-password">New password</Label><div className="relative"><LockKeyhole className="absolute left-3 top-3 h-4 w-4 text-slate-400" /><Input id="first-login-password" className="pl-9" type="password" minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required autoComplete="new-password" placeholder="At least 6 characters" /></div></div>
              <div className="space-y-2"><Label htmlFor="first-login-confirm">Confirm password</Label><Input id="first-login-confirm" type="password" minLength={6} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required autoComplete="new-password" /></div>
              <div className="space-y-2"><Label htmlFor="first-login-email">Recovery email</Label><div className="relative"><Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" /><Input id="first-login-email" className="pl-9" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" placeholder="you@example.com" /></div><p className="text-xs leading-5 text-slate-500">This email must be unique to your Track Labs account.</p></div>
              <Button type="submit" disabled={setup.isPending} className="w-full rounded-xl bg-cyan-700 py-5 text-white hover:bg-cyan-800">{setup.isPending ? "Saving…" : "Save and enter workspace"}</Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
